import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { formatDate, formatTime, formatTimeWithZone, zoneName } from '@/lib/format';
import { canRemoveVisit, FINISHED_STATUSES as FINISHED } from '@/lib/visitRules';
import {
  addDays,
  daysBetween,
  parseIsoDate,
  parseWallClock,
  toIsoDate,
  todayInZone,
  zonedCivilDate,
  zonedTimeToInstant,
  zonedWallClock,
} from '@/lib/timezone';
import type { components } from '@/api/generated/schema';
import {
  VisitGoneError,
  VisitRefusedError,
  useJobVisitMutations,
  type JobVisit,
  type VisitPlacement,
} from '@/routes/jobs/useJobVisits';

import { OnMyWayButton } from '@/components/appointments/OnMyWay';
import { AppointmentEmailList, AppointmentRecipients } from '@/components/appointments/AppointmentEmailList';
import type { AppointmentEmails, VisitOnMyWay } from '@/routes/appointments/useAppointmentEmails';

type Job = components['schemas']['JobResponse'];
type Technician = components['schemas']['UserSummaryResponse'];


/** The sentence for a failed visit write: the API's own refusal, a 404's, or a plain failure. */
function failureMessage(failure: Error, t: (key: string) => string, fallbackKey: string): string {
  if (failure instanceof VisitRefusedError) {
    return failure.message;
  }
  return t(failure instanceof VisitGoneError ? 'jobDetail.visits.gone' : fallbackKey);
}

/**
 * A job's visits, and — for dispatch — adding, moving and removing them one at
 * a time (M2-52, pass B2a). A water-heater swap has one; a three-day repipe has
 * three, possibly with a different technician each day (OD-04).
 *
 * Everyone who can open the job sees the list, because a technician booked on
 * Tuesday needs to know about Monday and Wednesday. Only `jobs.assign` edits,
 * the API's own gate, and nothing is offered that the API would refuse:
 * nothing on finished work, and no Remove on the only visit of a job that
 * someone is driving to or working.
 *
 * Every time is on the APPOINTMENT's clock — `JobDetailResponse.appointmentTimezone`,
 * the property's zone, else the account's (M2-21) — read and written, with
 * the zone named: a dispatcher in another zone books 8am where the customer
 * is, and reads the same 8am the customer's emails say.
 *
 * <b>Appointment emails (M2-21).</b> Each visit row offers "On my way" to the
 * visit's own technician and to `jobs.assign` (the API's rule), on live work
 * only; the API says whether it can go now and why not (`onMyWay`). The
 * office (`appointmentEmails` non-null, rule 5c) also sees, under each visit,
 * the emails about it, and under the list who the emails go to and the
 * booking's own emails. Those times are on the same clock as the visits
 * above them, each with its zone named — the clock the API's own sentences
 * ("…sent at 10:00 AM") are on.
 */
export function JobVisits({
  job,
  timeZone,
  canEdit,
  technicians,
  onAnnounce,
  appointmentEmails = null,
  onMyWay = [],
  currentUserId,
}: {
  job: Job;
  /** The appointment's clock (the property's, else the account's) — not the viewer's. */
  timeZone: string;
  /** `jobs.assign`. The finished-work rule is applied here, not by the caller. */
  canEdit: boolean;
  technicians: Technician[];
  onAnnounce: (message: string) => void;
  /** The office's view of the job's appointment emails; null for anyone else. */
  appointmentEmails?: AppointmentEmails | null;
  /** Per visit: whether THIS caller can send "on my way" now, and why not. */
  onMyWay?: readonly VisitOnMyWay[];
  /** The signed-in user, to offer "On my way" on their own visits. */
  currentUserId?: string;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const { remove } = useJobVisitMutations(job.id);
  const editable = canEdit && !FINISHED.includes(job.status);
  const live = !FINISHED.includes(job.status);
  const visitIds = new Set(job.visits.map((visit) => visit.id));
  // The booking's own emails: the confirmation, a whole-job cancellation, and
  // anything about a visit that has since been removed (its row is gone).
  const bookingEmails =
    appointmentEmails?.emails.filter((email) => email.visitId === null || !visitIds.has(email.visitId)) ?? [];

  const [editing, setEditing] = useState<JobVisit | 'new' | null>(null);
  const [removing, setRemoving] = useState<JobVisit | null>(null);
  const [removeError, setRemoveError] = useState('');

  const dateOf = (visit: JobVisit) =>
    formatDate(visit.scheduledStart, { dateStyle: 'medium', timeZone });
  // An end on a LATER day names that day: "8:00 AM – 5:00 PM" under Monday's
  // date would say a Monday-to-Wednesday visit is a Monday one. The zone is
  // named once, on the last time — and on both when they differ (a range over
  // a daylight-saving change), so neither is read on the wrong offset.
  const timesOf = (visit: JobVisit) => {
    if (!visit.scheduledEnd) {
      return t('jobDetail.visits.timeFrom', { start: formatTimeWithZone(visit.scheduledStart, timeZone) });
    }
    const start =
      zoneName(visit.scheduledStart, timeZone) === zoneName(visit.scheduledEnd, timeZone)
        ? formatTime(visit.scheduledStart, timeZone)
        : formatTimeWithZone(visit.scheduledStart, timeZone);
    const end = formatTimeWithZone(visit.scheduledEnd, timeZone);
    const sameDay =
      daysBetween(
        zonedCivilDate(new Date(visit.scheduledStart), timeZone),
        zonedCivilDate(new Date(visit.scheduledEnd), timeZone),
      ) === 0;
    return sameDay
      ? t('jobDetail.visits.timeRange', { start, end })
      : t('jobDetail.visits.timeRangeDays', {
          start,
          endDate: formatDate(visit.scheduledEnd, { dateStyle: 'medium', timeZone }),
          end,
        });
  };

  function confirmRemove() {
    if (!removing) {
      return;
    }
    setRemoveError('');
    remove.mutate(removing.id, {
      onSuccess: () => {
        onAnnounce(t('jobDetail.visits.removed', { date: dateOf(removing) }));
        setRemoving(null);
      },
      onError: (failure) => setRemoveError(failureMessage(failure, t, 'jobDetail.visits.removeFailed')),
    });
  }

  return (
    <section aria-labelledby={headingId} className="space-y-3 rounded-lg border p-4" data-testid="job-visits">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id={headingId} className="text-base font-semibold">
          {t('jobDetail.visits.heading')}
        </h2>
        {editable ? (
          <Button type="button" size="sm" onClick={() => setEditing('new')}>
            {t('jobDetail.visits.add')}
          </Button>
        ) : null}
      </div>

      {job.visits.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('jobDetail.visits.empty')}</p>
      ) : (
        <ul className="divide-y">
          {job.visits.map((visit, index) => {
            const label =
              job.visits.length > 1
                ? t('jobDetail.visits.numbered', {
                    number: index + 1,
                    total: job.visits.length,
                    date: dateOf(visit),
                  })
                : dateOf(visit);
            const onMyWayStatus = onMyWay.find((entry) => entry.visitId === visit.id);
            // The API's own rule for who may press it: the visit's technician,
            // or `jobs.assign`. Anyone else is not offered a button that only
            // ever says no.
            const offerOnMyWay =
              live && onMyWayStatus !== undefined && (canEdit || (currentUserId !== undefined && visit.assignedUserId === currentUserId));
            const visitEmails = appointmentEmails?.emails.filter((email) => email.visitId === visit.id) ?? [];
            return (
            <li
              key={visit.id}
              className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
              data-testid="job-visit"
            >
              <div>
                <p className="font-medium">{label}</p>
                <p className="text-[var(--color-muted-foreground)]">
                  {timesOf(visit)} · {visit.assignedUserName ?? t('jobDetail.visits.nobody')}
                </p>
              </div>
              {offerOnMyWay || editable ? (
                <div className="flex flex-wrap items-start justify-end gap-2">
              {offerOnMyWay ? (
                <OnMyWayButton
                  jobId={job.id}
                  status={onMyWayStatus}
                  emails={appointmentEmails}
                  visitLabel={label}
                  onAnnounce={onAnnounce}
                />
              ) : null}
              {editable ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={t('jobDetail.visits.editLabel', { date: dateOf(visit) })}
                    onClick={() => setEditing(visit)}
                  >
                    {t('jobDetail.visits.edit')}
                  </Button>
                  {!canRemoveVisit(job.visits.length, job.status) ? null : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={t('jobDetail.visits.removeLabel', { date: dateOf(visit) })}
                    onClick={() => {
                      setRemoveError('');
                      setRemoving(visit);
                    }}
                  >
                    {t('jobDetail.visits.remove')}
                  </Button>
                  )}
                </div>
              ) : null}
                </div>
              ) : null}
              {/* Under the row, full width: the emails about this visit. */}
              {appointmentEmails && visitEmails.length > 0 ? (
                <div className="w-full">
                  <AppointmentEmailList
                    emails={visitEmails}
                    recipients={appointmentEmails}
                    timeZone={timeZone}
                    testId={`visit-emails-${visit.id}`}
                  />
                </div>
              ) : null}
            </li>
            );
          })}
        </ul>
      )}

      {appointmentEmails ? (
        <div className="space-y-2 border-t pt-3" data-testid="appointment-emails">
          <h3 className="text-sm font-semibold">{t('appointmentEmails.jobHeading')}</h3>
          <AppointmentRecipients recipients={appointmentEmails} />
          {bookingEmails.length > 0 ? (
            <AppointmentEmailList
              emails={bookingEmails}
              recipients={appointmentEmails}
              timeZone={timeZone}
              testId="booking-emails"
            />
          ) : null}
        </div>
      ) : null}

      {editing ? (
        <VisitDialog
          job={job}
          visit={editing === 'new' ? undefined : editing}
          timeZone={timeZone}
          technicians={technicians}
          onClose={() => setEditing(null)}
          onAnnounce={onAnnounce}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          titleId={`${headingId}-remove`}
          title={t('jobDetail.visits.removeTitle', { date: dateOf(removing) })}
          description={
            job.visits.length === 1
              ? t('jobDetail.visits.removeLastDescription')
              : t('jobDetail.visits.removeDescription')
          }
          confirmLabel={t('jobDetail.visits.remove')}
          destructive
          isPending={remove.isPending}
          errorMessage={removeError || undefined}
          onConfirm={confirmRemove}
          onClose={() => setRemoving(null)}
          testId="remove-visit-dialog"
        />
      ) : null}
    </section>
  );
}

type Field = 'date' | 'start' | 'end' | 'endDate';

/**
 * Add a visit or change one. A new visit is pre-filled as "the next day, same
 * hours, same person" when the job already has visits — the repipe's Tuesday
 * is almost always Monday again — but never before today, since a callback
 * weeks later is not booked in the past. With no visits it is today at nine,
 * booked on the job's lead.
 *
 * ⚠️ The end has its own DATE. The API allows a visit to end on a later day,
 * and pass A copied M1's jobs across as they were, the 23:00–01:00 call
 * included. A dialog that built the end on the start's day would refuse to
 * save such a visit, or silently cut a Monday-to-Wednesday one back to
 * Monday. Moving the start date carries the end date with it, so changing
 * the day keeps the visit's length.
 */
function VisitDialog({
  job,
  visit,
  timeZone,
  technicians,
  onClose,
  onAnnounce,
}: {
  job: Job;
  visit?: JobVisit;
  timeZone: string;
  technicians: Technician[];
  onClose: () => void;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const fieldId = useId();
  const { add, move } = useJobVisitMutations(job.id);
  const saving = visit ? move : add;

  const template = visit ?? job.visits.at(-1);
  const today = todayInZone(timeZone);
  const dayAfterTemplate = template
    ? addDays(zonedCivilDate(new Date(template.scheduledStart), timeZone), 1)
    : null;
  const initialDate = visit
    ? zonedCivilDate(new Date(visit.scheduledStart), timeZone)
    : dayAfterTemplate && daysBetween(today, dayAfterTemplate) > 0
      ? dayAfterTemplate
      : today;
  // How many days after its start the template ends (0 for a same-day visit).
  const templateSpanDays = template?.scheduledEnd
    ? daysBetween(
        zonedCivilDate(new Date(template.scheduledStart), timeZone),
        zonedCivilDate(new Date(template.scheduledEnd), timeZone),
      )
    : 0;

  const [date, setDate] = useState(toIsoDate(initialDate));
  const [endDate, setEndDate] = useState(toIsoDate(addDays(initialDate, templateSpanDays)));
  // The last start date that parsed. Clearing the box and typing a new date
  // passes through "" on the way, and measuring the move from that would lose
  // it — the end date would stay behind on the old day.
  const lastValidDate = useRef(initialDate);
  const [start, setStart] = useState(
    template ? zonedWallClock(new Date(template.scheduledStart), timeZone) : '09:00',
  );
  const [end, setEnd] = useState(
    template?.scheduledEnd ? zonedWallClock(new Date(template.scheduledEnd), timeZone) : '',
  );
  const [technicianId, setTechnicianId] = useState(
    (template ? template.assignedUserId : job.assignedUserId) ?? '',
  );
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [formError, setFormError] = useState('');

  // A technician booked on the visit who is no longer on the active roster
  // (disabled since) is still shown, so opening the visit to move its time does
  // not silently read as "nobody". Saving with them is refused by the API.
  const bookedElsewhere =
    technicianId && !technicians.some((technician) => technician.id === technicianId)
      ? { id: technicianId, name: template?.assignedUserName ?? technicianId }
      : null;

  /** A new start date carries the end date with it, keeping the visit's length. */
  function changeDate(value: string) {
    const to = parseIsoDate(value);
    const currentEnd = parseIsoDate(endDate);
    if (to) {
      if (currentEnd) {
        setEndDate(toIsoDate(addDays(currentEnd, daysBetween(lastValidDate.current, to))));
      }
      lastValidDate.current = to;
    }
    setDate(value);
  }

  function submit() {
    const next: Partial<Record<Field, string>> = {};
    const civil = parseIsoDate(date);
    const endCivil = parseIsoDate(endDate);
    const startMinute = parseWallClock(start);
    const endMinute = end ? parseWallClock(end) : null;
    if (!civil) {
      next.date = t('jobDetail.visits.form.dateRequired');
    }
    if (startMinute === null) {
      next.start = t('jobDetail.visits.form.startRequired');
    }
    if (end && endMinute === null) {
      next.end = t('jobDetail.visits.form.endInvalid');
    } else if (endMinute !== null && !endCivil) {
      next.endDate = t('jobDetail.visits.form.endDateRequired');
    }

    const at = (day: typeof civil, minute: number) =>
      zonedTimeToInstant(day!, Math.floor(minute / 60), minute % 60, timeZone);
    const startAt = civil && startMinute !== null ? at(civil, startMinute) : null;
    const endAt = endCivil && endMinute !== null ? at(endCivil, endMinute) : null;
    if (startAt && endAt && endAt.getTime() <= startAt.getTime()) {
      next.end = t('jobDetail.visits.form.endAfterStart');
    }
    setErrors(next);
    setFormError('');
    if (Object.keys(next).length > 0 || !startAt) {
      return;
    }

    const placement: VisitPlacement = {
      scheduledStart: startAt.toISOString(),
      scheduledEnd: endAt ? endAt.toISOString() : null,
      assignedUserId: technicianId || null,
    };
    const when = formatDate(placement.scheduledStart, { dateStyle: 'medium', timeZone });
    const callbacks = {
      onSuccess: () => {
        onAnnounce(
          visit ? t('jobDetail.visits.moved', { date: when }) : t('jobDetail.visits.added', { date: when }),
        );
        onClose();
      },
      onError: (failure: Error) => setFormError(failureMessage(failure, t, 'jobDetail.visits.form.saveFailed')),
    };
    if (visit) {
      move.mutate({ visitId: visit.id, placement }, callbacks);
    } else {
      add.mutate(placement, callbacks);
    }
  }

  // The zone as it is named on the day being booked (summer time or not).
  const chosenDay = parseIsoDate(date);
  const zoneDay = chosenDay ? zonedTimeToInstant(chosenDay, 12, 0, timeZone) : new Date();

  const fieldError = (field: Field) =>
    errors[field] ? (
      <span id={`${fieldId}-${field}-error`} className="text-xs text-[var(--color-destructive)]">
        {errors[field]}
      </span>
    ) : null;

  return (
    <Dialog
      titleId={titleId}
      title={visit ? t('jobDetail.visits.form.editTitle') : t('jobDetail.visits.form.addTitle')}
      description={job.customerName}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={saving.isPending} onClick={submit}>
            {saving.isPending ? t('jobDetail.visits.form.saving') : t('jobDetail.visits.form.save')}
          </Button>
        </>
      }
    >
      <p id={`${fieldId}-zone`} className="mb-3 text-sm text-[var(--color-muted-foreground)]" data-testid="visit-zone">
        {t('jobDetail.appointmentClock', { zone: zoneName(zoneDay, timeZone), id: timeZone })}
      </p>
      <fieldset disabled={saving.isPending} className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1 text-sm">
          <label className="flex flex-col gap-1">
            <span>{t('jobDetail.visits.form.date')}</span>
            <input
              type="date"
              className={FIELD_CLASS}
              value={date}
              onChange={(event) => changeDate(event.target.value)}
              aria-invalid={errors.date ? true : undefined}
              aria-describedby={errors.date ? `${fieldId}-date-error` : undefined}
            />
          </label>
          {fieldError('date')}
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span>{t('jobDetail.visits.form.technician')}</span>
          <select
            className={FIELD_CLASS}
            value={technicianId}
            onChange={(event) => setTechnicianId(event.target.value)}
          >
            <option value="">{t('jobDetail.visits.nobody')}</option>
            {bookedElsewhere ? <option value={bookedElsewhere.id}>{bookedElsewhere.name}</option> : null}
            {technicians.map((technician) => (
              <option key={technician.id} value={technician.id}>
                {technician.name}
              </option>
            ))}
          </select>
        </label>
        <div className="flex flex-col gap-1 text-sm">
          <label className="flex flex-col gap-1">
            <span>{t('jobDetail.visits.form.start')}</span>
            <input
              type="time"
              className={FIELD_CLASS}
              value={start}
              onChange={(event) => setStart(event.target.value)}
              aria-invalid={errors.start ? true : undefined}
              aria-describedby={errors.start ? `${fieldId}-zone ${fieldId}-start-error` : `${fieldId}-zone`}
            />
          </label>
          {fieldError('start')}
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <label className="flex flex-col gap-1">
            <span>{t('jobDetail.visits.form.end')}</span>
            <input
              type="time"
              className={FIELD_CLASS}
              value={end}
              onChange={(event) => setEnd(event.target.value)}
              aria-invalid={errors.end ? true : undefined}
              aria-describedby={errors.end ? `${fieldId}-zone ${fieldId}-end-error` : `${fieldId}-zone`}
            />
          </label>
          {fieldError('end')}
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <label className="flex flex-col gap-1">
            <span>{t('jobDetail.visits.form.endDate')}</span>
            <input
              type="date"
              className={FIELD_CLASS}
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              aria-invalid={errors.endDate ? true : undefined}
              aria-describedby={errors.endDate ? `${fieldId}-endDate-error` : undefined}
            />
          </label>
          {fieldError('endDate')}
        </div>
      </fieldset>
      {formError ? (
        <p role="alert" className="mt-3 text-sm text-[var(--color-destructive)]">
          {formError}
        </p>
      ) : null}
    </Dialog>
  );
}
